import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
    debounce,
    fetchData,
    formatDate,
    formatNumber,
    formatTargetValue,
    getEncryptedRoute,
    getTargetMeta,
    toasterrormsg,
    toastsuccessmsg,
    useMenuIdPermissions,
} from '../../../reusable'
import Swal from 'sweetalert2'
import { useAuthStore } from '../store/UseAuthStore'
import { KTIcon } from '../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import DataTable from 'react-data-table-component'
import Loader from '../components/Loader'
import { Tr } from '../../../_metronic/i18n/Tr'
import { Modal } from 'react-bootstrap'
import { regionList, interestList } from '../types/general'

const SPONSER_STATUSES = [
    { key: 'UNDER_REVIEW', label: 'Under Review' },
    { key: 'ACTIVATED', label: 'Active Ads' },
    { key: 'APPROVED', label: 'Approved' },
    { key: 'PAUSED', label: 'Paused' },
    { key: 'REJECTED', label: 'Rejected' },
    { key: 'COMPLETED', label: 'Completed' },
    { key: 'DRAFT', label: 'Drafts' },
]

const SearchAdvertisements: FC = () => {
    let t = Tr()
    const { urlData, menuId, menuIdEdit, menuIdDelete, menuIdView } = useMenuIdPermissions()
    const [selectedStatus, setSelectedStatus] = useState(urlData?.status || 'UNDER_REVIEW')
    const navigate = useNavigate()
    const { setReviewCount } = useAuthStore()


    const [sponserList, setSponserList] = useState([])
    const [regionList, setRegionList] = useState<regionList[]>([]);
    const [interestList, setInterestList] = useState<interestList[]>([]);
    const [inputValue, setInputValue] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [totalRecords, setTotalRecords] = useState(0)
    const [limit, setLimit] = useState(10)
    const [offset, setOffset] = useState(0)
    const [sortField, setSortField] = useState('')
    const [sortOrder, setSortOrder] = useState('')
    const [isDataLoaded, setIsDataLoaded] = useState(false)
    const [focus, setFocus] = useState('')
    const [activeSponserCount, setActiveSponserCount] = useState(0)
    const [pausedSponserCount, setPausedSponserCount] = useState(0)
    const tmp = sessionStorage.getItem('admin')
    const adminId = tmp ? JSON.parse(tmp).adminId : ''

    // Targeting modal state
    const [showTargetingModal, setShowTargetingModal] = useState(false)
    const [selectedTargets, setSelectedTargets] = useState<any[]>([])


    const sponserListApiCall = async () => {
        try {
            if (!isDataLoaded) {
                setIsLoading(true)
            }
            const param = {
                limit: limit,
                offset: offset,
                status: selectedStatus,
                term: searchTerm.trim(),
                sortBy: sortField,
                sort: sortOrder,
            }
            const response = await fetchData('admin/sponser/list', param)

            if (response.status == '200') {
                const data = response.data
                setTotalRecords(data.totalRecord || 0)
                setSponserList(data.list || [])
                if (data.activeSponserCount !== undefined) setActiveSponserCount(data.activeSponserCount)
                if (data.pausedSponserCount !== undefined) setPausedSponserCount(data.pausedSponserCount)
                setIsDataLoaded(true)
            } else {

                toasterrormsg(response?.message || 'An error occurred')
                setSponserList([])
                setTotalRecords(0)
            }
        } catch (error) {
            console.error('Error fetching sponser list:', error)
            toasterrormsg('Failed to fetch search ads')
            setSponserList([])
            setTotalRecords(0)
        } finally {
            setIsDataLoaded(true)

            setIsLoading(false)
        }
    }

    // region list api call
    const regionListApiCall = async () => {
        try {
            const response = await fetchData('vendor/public/regionList', {})
            if (response.status == '200') {
                const data = response.data
                setRegionList(Array.isArray(data) ? data : []);
            } else {
                toasterrormsg(response?.message || 'An error occurred')
                setRegionList([])
            }
        } catch (error) {
            console.error('Error fetching region list:', error)
            setRegionList([])

        }
    }

    const interestListApiCall = async () => {
      try {
        const response = await fetchData('admin/interestlist', {})
        if (response.status == '200') {
          const data = response.data
          setInterestList(Array.isArray(data) ? data : []);
        } else {
          console.error(response?.message || 'An error occurred')
          setInterestList([])
        }
      } catch (error) {
        console.error('Error fetching interest list:', error)
        setInterestList([])
      }
    }

    const changeSposerStatus = async (action: string, sponserId: number, reason?: string) => {
        let swalOptions: any = {
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }

        if (action === 'REJECTED') {
            swalOptions = {
                ...swalOptions,
                title: t('Are you sure you want to reject this search ad?'),
                input: 'text',
                inputLabel: t('Reason'),
                inputPlaceholder: t('Enter reject reason'),
                inputValidator: (value: string) => {
                    if (!value) return t('Reject reason is required')
                },
            }
        } else if (action === 'APPROVED') {
            swalOptions.title = t('Are you sure you want to approve this search ad?')
        }

        const result = await Swal.fire(swalOptions)
        if (!result.isConfirmed) return

        const param: any = {
            adminId: adminId,
            sponserId: sponserId,
            status: action,
        }
        if (action === 'REJECTED') {
            param.rejectReason = result.value
        }

        try {
            setIsLoading(true)
            const response = await fetchData('admin/sponser/review', param)

            if (String(response?.status) === '200') {
                sponserListApiCall()
                toastsuccessmsg(response.message)
            } else {
                toasterrormsg(response?.message || 'Action failed')
            }
        } catch (error) {
            toasterrormsg('Something went wrong. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const pauseResumeSponser = async (action: 'PAUSED' | 'ACTIVATED', sponserId: number) => {
        const isPause = action === 'PAUSED'
        const result = await Swal.fire({
            icon: 'warning',
            title: isPause
                ? t('Are you sure you want to pause this search ad?')
                : t('Are you sure you want to resume this search ad?'),
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        })
        if (!result.isConfirmed) return

        try {
            setIsLoading(true)
            const response = await fetchData('admin/sponser/pauseresume', {
                adminId: adminId,
                sponserId: sponserId,
                status: action,
            })

            if (String(response?.status) === '200') {
                sponserListApiCall()
                toastsuccessmsg(response.message)
            } else {
                toasterrormsg(response?.message || 'Action failed')
            }
        } catch (error) {
            toasterrormsg('Something went wrong. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    // const confirmDeleteSponser = (sponserId: any) => {
    //     Swal.fire({
    //         title: 'Are you sure?',
    //         html: `
    //         <p>Are you sure you want to delete this sponser?</p>
    //         <small style="color:#6c757d">
    //             Once deleted, this sponser will be permanently removed.
    //         </small>
    //         `,
    //         icon: 'warning',
    //         showCancelButton: true,
    //         confirmButtonText: 'Yes',
    //         cancelButtonText: 'No',
    //     }).then(async (result) => {
    //         if (result.isConfirmed) {
    //             try {
    //                 setIsLoading(true)
    //                 const param = { sponserId }
    //                 const response = await fetchData('admin/sponser/delete', param, 'DELETE')

    //                 if (response.status == '200') {
    //                     toastsuccessmsg(response.message || 'Deleted Successfully')
    //                     sponserListApiCall()
    //                 } else {
    //                     toasterrormsg(response.message)
    //                 }
    //             } catch (error) {
    //                 console.error('Error deleting sponser:', error)
    //                 toasterrormsg('Failed to delete sponser')
    //             } finally {
    //                 setIsLoading(false)
    //             }
    //         }
    //     })
    // }
    const openTargetingModal = (row: any) => {
        setSelectedTargets(row.target || [])
        setShowTargetingModal(true)
    }

    const sponserSearch = (text: string, isfoc: any) => {
        setInputValue(text)
        setFocus(isfoc)
        debouncedSearch(text)
    }

    const debouncedSearch = useRef(
        debounce((value: string) => {
            setSearchTerm(value)
            setOffset(0)
        }, 500)
    ).current

    useEffect(() => {
        return () => debouncedSearch.cancel()
    }, [])

    const handleSort = (column: any, sortDirection: string) => {
        try {
            const fieldName = column.selector ? String(column.selector).split('.').pop() : ''
            setSortField(fieldName || '')
            setSortOrder(sortDirection === 'asc' ? 'asc' : 'desc')
        } catch (error) {
            console.error('Error in sorting:', error)
        }
    }

    const handleTabChange = (status: string) => {
        // Clicking the already-active tab would clear the list without changing any
        // dependency of the fetch effect, leaving the table permanently empty.
        if (status === selectedStatus) return

        setSponserList([])
        setTotalRecords(0)
        setIsDataLoaded(false)
        setSelectedStatus(status)
        setOffset(0)
        setSearchTerm('')
        setInputValue('')
        setSortField('')
        setSortOrder('')

        const newUrl = getEncryptedRoute('', { menuId, status })
        navigate({ search: newUrl.split('?')[1] }, { replace: false })
    }

    const CustomHeader = (props: any) => {
        const inputRef: any = useRef(null)

        const handleChange = async (e: any) => {
            await props.customSearch(e, 1)
        }
        useEffect(() => {
            if (inputRef.current && props.isfoc === 1) {
                inputRef.current.focus()
            }
        }, [props.isfoc])

        return (
            <div className=' d-flex justify-content-between w-100 my-5'>
                <div className='d-flex align-items-center position-relative my-1 '>
                    <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
                    <input
                        type='text'
                        data-kt-user-table-filter='search'
                        className='form-control form-control-solid w-250px ps-14'
                        placeholder={t('Search')}
                        value={props.search}
                        onChange={(e: any) => handleChange(e.target.value)}
                        ref={inputRef}
                    />
                </div>
            </div>
        )
    }

    // Dynamically compute action column width based on which buttons are visible
    const actionColumnWidth = useMemo(() => {
        if (selectedStatus === 'UNDER_REVIEW') {
            // View + Edit + Approve + Reject = 4 buttons
            return '380px'
        } else if (selectedStatus === 'ACTIVATED') {
            // View + Pause = 2 buttons
            return '240px'
        } else if (selectedStatus === 'PAUSED') {
            // View + Resume = 2 buttons
            return '200px'
        } else if (selectedStatus === 'DRAFT') {
            // View + Edit = 2 buttons
            return '200px'
        }
        else if (selectedStatus === 'APPROVED') {
            // View + Edit = 2 buttons
            return '200px'
        }
        else if (selectedStatus === 'REJECTED') {
            return '110px'
        }
        else if (selectedStatus === 'COMPLETED') {
            return '110px'
        }
        return '290px'

    }, [selectedStatus])

    const hasActions = selectedStatus === 'UNDER_REVIEW' || selectedStatus === 'APPROVED' || selectedStatus === 'PAUSED' || selectedStatus === 'ACTIVATED' || selectedStatus === 'DRAFT' || selectedStatus === 'REJECTED' || selectedStatus === 'COMPLETED'
    const showDateRange = ['APPROVED', 'ACTIVATED', 'COMPLETED', 'PAUSED'].includes(selectedStatus)

    const column = useMemo(
        () => [

            {
                name: t('Property Name'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{row.en_parentName ? row.en_parentName : (row.en_name || '-')}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.en_parentName || row.en_name,
                width: '180px',

            },
            {
                name: t('Listing Name'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{row.en_parentName ? (row.en_name || '-') : '-'}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.en_parentName ? row.en_name : '',
                width: '180px',

            },
            {
                name: t('Vendor Category'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{row.en_categoryName || '-'}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.en_categoryName,
                width: '200px',
            },
            {
                name: t('Vendor Name'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{row.fullName || '-'}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.fullName,
                width: '150px',

            },
            {
                name: t('Duration'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>
                            {row?.duration != null
                                ? `${row.duration}${row?.durationType ? ` ${row.durationType}` : ''}`
                                : '- -'}
                        </div>                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.duration,
                width: '130px',

            },
            {
                name: t('Payment Amount'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{row.price ? formatNumber(parseFloat(row.price)) : '-'}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.price,
                width: '150px',

            },
            {
                name: t('Targeting Details'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <button
                            className='btn btn-light-primary btn-sm d-flex align-items-center justify-content-center gap-1 ps-2 pe-4 py-2 rounded-pill'
                            title='View Targeting Details'
                            onClick={() => openTargetingModal(row)}
                        >
                            <KTIcon iconName='target' className='fs-4' iconType='solid' />
                            <span className='fs-7'>{t('Targets')}</span>
                        </button>
                    </div>
                ),
                sortable: false,
                width: '130px',
            },
            ...(showDateRange
                ? [
                    {
                        name: t('Start Date'),
                        cell: (row: any) => (
                            <div className='my-3'>
                                <div className='fw-bold fs-6'>{row.startDate ? formatDate(row.startDate, 'DD-MM-YYYY') : <span className='text-muted'>—</span>}</div>
                            </div>
                        ),
                        sortable: true,
                        selector: (row: any) => row.startDate,
                        width: '130px',
                    },
                    {
                        name: t('End Date'),
                        cell: (row: any) => (
                            <div className='my-3'>
                                <div className='fw-bold fs-6'>{row.endDate ? formatDate(row.endDate, 'DD-MM-YYYY') : <span className='text-muted'>—</span>}</div>
                            </div>
                        ),
                        sortable: true,
                        selector: (row: any) => row.endDate,
                        width: '130px',
                    },
                ]
                : []),
            {
                name: t('Created Date'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{row.created ? formatDate(row.created, 'DD-MM-YYYY') : '-'}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.created,
            },
            ...(hasActions && menuIdEdit
                ? [
                    {
                        name: t('Action'),
                        cell: (row: any) => (
                            <div className='d-flex gap-2 flex-wrap'>
                                <Link
                                    to={getEncryptedRoute('../searchadvertisement/view/', {
                                        menuId: menuId,
                                        sponserId: row.sponserId,

                                    })}
                                    className='btn btn-light-primary btn-sm d-flex align-items-center px-3 py-2 rounded-pill '
                                    title='View Search Ad'
                                >
                                        <KTIcon iconName='eye' className='fs-3' iconType='solid' />
                                        {t('View')}
                                    </Link>

                                {['DRAFT', 'UNDER_REVIEW', 'APPROVED'].includes(selectedStatus) && (
                                    <Link
                                        to={getEncryptedRoute('../searchadvertisement/addedit/', {
                                            menuId: menuId,
                                            sponserId: row.sponserId,
                                        })}
                                        className='btn btn-light-warning btn-sm d-flex align-items-center px-3 py-2 rounded-pill '
                                        title='Edit Sponser'
                                    >
                                        <KTIcon iconName='pencil' className='fs-3' iconType='solid' />
                                        {t('Edit')}
                                    </Link>
                                )}

                                {selectedStatus === 'UNDER_REVIEW' && (
                                    <>
                                        <button
                                            className='btn btn-light-success btn-sm d-flex align-items-center px-3 py-2 rounded-pill '
                                            title='Approve Sponser'
                                            onClick={() => changeSposerStatus('APPROVED', row.sponserId)}
                                        >
                                            <KTIcon iconName='check-circle' className='fs-3' iconType='solid' />
                                            {t('Approve')}
                                        </button>

                                        <button
                                            className='btn btn-light-danger btn-sm d-flex align-items-center px-3 py-2 rounded-pill '
                                            title='Reject Sponser'
                                            onClick={() => changeSposerStatus('REJECTED', row.sponserId)}
                                        >
                                            <KTIcon iconName='cross-circle' className='fs-3' iconType='solid' />
                                            {t('Reject')}
                                        </button>
                                    </>
                                )}

                                {selectedStatus === 'ACTIVATED' && (
                                    <>
                                        <button
                                            className='btn btn-light-warning btn-sm d-flex align-items-center px-3 py-2 rounded-pill '
                                            title='Pause Sponser'
                                            onClick={() => pauseResumeSponser('PAUSED', row.sponserId)}
                                        >
                                            <KTIcon iconName='row-vertical' className='fs-3 text-warning' iconType='solid' />
                                            {t('Pause Search Ad')}
                                        </button>
                                        {/* <button
                                            className='btn btn-light-success btn-sm d-flex align-items-center px-3 py-2 rounded-pill '
                                            title='Resume Sponser'
                                            onClick={() => pauseResumeSponser('RESUME', row.sponserId)}
                                        >
                                            <KTIcon iconName='to-right' className='fs-3' iconType='solid' />
                                            {t('Resume')}
                                        </button> */}
                                    </>
                                )}

                                {selectedStatus === 'PAUSED' && (
                                    <button
                                        className='btn btn-light-success btn-sm d-flex align-items-center px-3 py-2 rounded-pill '
                                        title='Resume Sponser'
                                        onClick={() => pauseResumeSponser('ACTIVATED', row.sponserId)}
                                    >
                                        <KTIcon iconName='to-right' className='fs-3' iconType='solid' />
                                        {t('Resume')}
                                    </button>
                                )}

                                {/* {menuIdDelete && (
                                    <button
                                        className='btn btn-light-danger btn-sm d-flex align-items-center px-3 py-2 rounded-pill shadow-sm'
                                        title='Delete Sponser'
                                        onClick={() => confirmDeleteSponser(row.sponserId)}
                                    >
                                        <KTIcon iconName='trash' className='fs-3' iconType='solid' />
                                        {t('Delete')}
                                    </button>
                                )} */}
                            </div>
                        ),
                        width: actionColumnWidth,
                    },
                ]
                : []),
        ],
        [t, menuIdView, menuIdEdit, menuIdDelete, menuId, selectedStatus]
    )

    useEffect(() => {
        sponserListApiCall()
    }, [limit, offset, selectedStatus, sortField, sortOrder, searchTerm])

    const readAdsReview = async () => {
        const response: any = await fetchData('admin/readadsreview', { type: 'sponser' })
        if (response.status == '200') {
            setReviewCount(true)
        }
    }

    useEffect(() => {
        document.title = 'Insayr | Search Advertisements'
        regionListApiCall()
        interestListApiCall()
        readAdsReview()
    }, [])

    const handlePageChange = (newPage: number) => {
        try {
            const totalPages = Math.ceil(totalRecords / limit)

            if (newPage === 1) {
                setOffset(0)
            } else if (newPage === totalPages) {
                setOffset((totalPages - 1) * limit)
            } else {
                setOffset((newPage - 1) * limit)
            }
        } catch (error) {
            console.error('Error in page change:', error)
        }
    }

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        try {
            setLimit(newRowsPerPage)
            setOffset(0)
        } catch (error) {
            console.error('Error in rows per page change:', error)
        }
    }



    return (
        <>
            {/* Live Stats */}
            {isDataLoaded && (activeSponserCount > 0 || pausedSponserCount > 0) && (
                <div className='row g-3 mb-5'>
                    <div className='col-6 col-sm-4 col-lg-3'>
                        <div
                            className='d-flex align-items-center gap-3 p-4 rounded-3 h-100 cursor-pointer'
                            style={{ background: '#F0FDF4', border: `2px solid ${selectedStatus === 'ACTIVATED' ? '#22C55E' : '#BBF7D0'}` }}
                            onClick={() => handleTabChange('ACTIVATED')}
                        >
                            <div className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0' style={{ width: 44, height: 44, background: '#DCFCE7' }}>
                                <KTIcon iconName='verify' className='fs-2 text-success' iconType='solid' />
                            </div>
                            <div>
                                <div className='fw-bolder fs-2 text-success lh-1 mb-1'>{activeSponserCount}</div>
                                <div className='fw-semibold fs-7 text-gray-600'>{t('Active Search Ads')}</div>
                            </div>
                        </div>
                    </div>
                    <div className='col-6 col-sm-4 col-lg-3'>
                        <div
                            className='d-flex align-items-center gap-3 p-4 rounded-3 h-100 cursor-pointer'
                            style={{ background: '#FFFBEB', border: `2px solid ${selectedStatus === 'PAUSED' ? '#F59E0B' : '#FDE68A'}` }}
                            onClick={() => handleTabChange('PAUSED')}
                        >
                            <div className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0' style={{ width: 44, height: 44, background: '#FEF3C7' }}>
                                <KTIcon iconName='to-right' className='fs-2 text-warning' iconType='solid' />
                            </div>
                            <div>
                                <div className='fw-bolder fs-2 text-warning lh-1 mb-1'>{pausedSponserCount}</div>
                                <div className='fw-semibold fs-7 text-gray-600'>{t('Paused Search Ads')}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className='card mb-5 mb-xl-10'>
                <div className='card-body'>
                    <div className='d-flex overflow-auto'>
                        <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder my-2 flex-nowrap'>
                            {SPONSER_STATUSES.map((status) => (
                                <li className='nav-item' key={status.key}>
                                    <button
                                        className={`nav-link text-active-primary me-6 p-3 text-nowrap ${selectedStatus == status.key ? 'bg-success text-white rounded' : 'nav-link text-active-primary me-6 p-3 text-nowrap bg-primary text-white rounded'
                                            }`}
                                        onClick={() => handleTabChange(status.key)}
                                    >
                                        {t(status.label)}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            <ToastContainer />
            {isLoading && <Loader />}

            {selectedStatus === 'APPROVED' && isDataLoaded && (
                <div className='d-flex align-items-start gap-3 p-4 rounded-3 mb-5' style={{ background: '#FFF7ED', border: '1px solid #FED7AA' }}>
                    <KTIcon iconName='time' className='fs-1 text-warning flex-shrink-0 ' />
                    <div>
                        <div className='fw-bold fs-6 text-warning mb-1'>{t('Search Ads in Queue')}</div>
                        <p className='text-gray-700 fs-sm mb-0'>
                            {t('These search ads have been approved and are waiting in the queue. They will be automatically activated once their scheduled start date arrives. Search ads without a start date will be activated in order of approval.')}
                        </p>
                    </div>
                </div>
            )}

            {selectedStatus === 'ACTIVATED' && isDataLoaded && (
                <div className='d-flex align-items-start gap-3 p-4 rounded-3 mb-5' style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                    <KTIcon iconName='verify' className='fs-1 text-success flex-shrink-0 mt-1' />
                    <div>
                        <div className='fw-bold fs-5 text-success mb-1'>{t('Currently Running Search Ads')}</div>
                        <p className='text-gray-700 fs-6 mb-0'>
                            {t('These search ads are currently live and being displayed to users.')}
                        </p>
                    </div>
                </div>
            )}

            {selectedStatus === 'COMPLETED' && isDataLoaded && (
                <div className='d-flex align-items-start gap-3 p-4 rounded-3 mb-5' style={{ background: '#DBEAFE', border: '1px solid #93C5FD' }}>
                    <KTIcon iconName='calendar-tick' className='fs-1 text-primary flex-shrink-0 mt-1' />
                    <div>
                        <div className='fw-bold fs-5 text-primary mb-1'>{t('Completed Campaigns')}</div>
                        <p className='text-gray-700 fs-6 mb-0'>
                            {t('These search ads have finished their scheduled run and are no longer active.')}
                        </p>
                    </div>
                </div>
            )}

            {selectedStatus === 'PAUSED' && isDataLoaded && (
                <div className='d-flex align-items-start gap-3 p-4 rounded-3 mb-5' style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                    <KTIcon iconName='pause-circle' className='fs-1 text-warning flex-shrink-0 mt-1' />
                    <div>
                        <div className='fw-bold fs-5 text-warning mb-1'>{t('Paused Search Ads')}</div>
                        <p className='text-gray-700 fs-6 mb-0'>
                            {t('These search ads have been temporarily paused. They can be resumed to continue showing to users.')}
                        </p>
                    </div>
                </div>
            )}

            <DataTable
                columns={column}
                data={sponserList}
                pagination
                paginationServer={true}
                onChangePage={handlePageChange}
                paginationTotalRows={totalRecords}
                paginationPerPage={limit}
                onChangeRowsPerPage={handleRowsPerPageChange}
                paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
                sortServer={true}
                onSort={handleSort}
                subHeader={true}
                subHeaderComponent={<CustomHeader search={inputValue} isfoc={focus} customSearch={sponserSearch} />}
                paginationComponentOptions={{
                    rowsPerPageText: t('Rows Per Page'),
                }}
            />

            {/* Targeting Details Modal */}
            <Modal
                show={showTargetingModal}
                onHide={() => setShowTargetingModal(false)}
                centered
                size='lg'
            >
                <Modal.Header closeButton className='border-bottom-0 pb-0'>
                    <Modal.Title className='fw-bold fs-4'>
                        <KTIcon iconName='target' className='fs-2 text-primary me-2' iconType='solid' />
                        {t('Targeting Details')}

                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className='pt-4'>
                    {selectedTargets.length > 0 && selectedTargets[0]?.sponsertargetId !== null ? (
                        <div className='row g-3'>
                            {selectedTargets.map((target: any, idx: number) => {
                                const meta = getTargetMeta(target?.key)
                                const targetIcon = target?.iconName || meta.icon
                                const colorClass = meta.colorClass
                                const displayValue = formatTargetValue(target?.key, target?.value, regionList, interestList)
                                return (
                                    <div className='col-6 col-sm-4 col-md-3' key={idx}>
                                        <div
                                            className='text-center p-3 rounded-3 h-100'
                                            style={{ border: '1px solid #e9ecef', background: '#fafafa' }}
                                        >
                                            <div
                                                className={`d-flex align-items-center justify-content-center mx-auto mb-2 rounded-circle bg-light-${colorClass}`}
                                                style={{ width: 40, height: 40 }}
                                            >
                                                <KTIcon iconName={targetIcon} className={`fs-3 text-${colorClass}`} />
                                            </div>
                                            <div className='text-muted fw-bold fs-9 text-uppercase mb-1'>{target?.key || '-'}</div>
                                            <div className='fw-bold fs-7 text-dark text-break'>{displayValue}</div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        <div className='d-flex align-items-start gap-2 text-muted p-3'>
                            <KTIcon iconName='information-5' className='fs-3 flex-shrink-0 mt-1' />
                            <span className='fs-6'>{t('No specific targeting — search ad will be shown to all users')}</span>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className='border-top-0 pt-0'>
                    <button
                        className='btn btn-light btn-sm px-5'
                        onClick={() => setShowTargetingModal(false)}
                    >
                        {t('Close')}
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export { SearchAdvertisements }
